<script setup lang="ts">
import { storeToRefs } from 'pinia';
import LoyaltyHubSheet from './Sheet.vue';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';
import { hexAlpha } from '@/utils/levels';

// A tela de níveis aberta DENTRO de um modal por cima do hub Club Fidelidade —
// em vez de fechar o hub e navegar pra /vip/levels. A ROTA CONTINUA existindo e
// igual: aqui reaproveitamos as MESMAS views da página (`LevelsDesktopView` /
// `LevelsMobileView`), que são autossuficientes (leem tudo do
// `useLevelsProgress`).
//
// O ganho é o caminho de volta: fechar o painel devolve o jogador ao hub, e não
// a um histórico de navegação — é o que faz o clube parecer um lugar só.
//
// Estado só em memória (nada na URL), igual roletinha, rewards e loja.
const store = useLoyaltyHubStore();
const { isLevelsOpen } = storeToRefs(store);

const { isDesktop } = useLayout();

// O MESMO portão da página (`pages/vip/levels.vue`): sem a gamificação ligada e
// pronta, as views não têm o que mostrar. Sem isto o painel abriria vazio
// justamente quando a Smartico não subiu.
//
// Na prática o caminho feliz já está coberto — o cartão que abre este painel só
// aparece com a gamificação pronta —, mas o deep-link não passa pelo cartão.
const { initSmarticoError, isReadyGamefication } =
  storeToRefs(useSmarticoInit());

const { isGamificationLevelsActived } = storeToRefs(useSmarticoConfigs());

const canRender = computed(
  () =>
    isGamificationLevelsActived.value &&
    !initSmarticoError.value &&
    isReadyGamefication.value,
);

// O glow do elo sai do PAINEL e não da view: dentro do modal a view só alcança
// a área de rolagem, e o brilho morria na linha da topbar — a faixa da logo
// ficava chapada, com uma emenda visível. Pintado no painel ele nasce atrás da
// topbar, que é o que o desenho pede.
//
// A cor é a do elo DO JOGADOR. Na página o fundo acompanha o elo em foco no
// pódio, mas esse foco mora dentro da view (cada chamada de `useLevelsProgress`
// tem seu próprio estado de navegação) — e ler daqui criaria um segundo estado,
// que não seguiria nada.
const { tier } = useLevelsProgress();

const panelBackground = computed(
  () =>
    `radial-gradient(76% 42% at 50% 0%,${hexAlpha(tier.value.color, 0.22)} 0%,#000717 46%,#000717 76%)`,
);

// As partículas do casco saem de cena aqui (`particles-intensity="0"` no
// template): a view de níveis traz as PRÓPRIAS fagulhas atrás do herói, na cor
// do elo, e na rota elas eram a única camada. Dentro do painel as duas se
// somavam — e o fundo transparente que damos à view (ver o `:deep` abaixo)
// ainda deixava as do casco atravessarem o conteúdo todo.
//
// Monta só depois do slide-up: as views são pesadas (pódio, carrossel dos 14
// elos, comparativo) e montar durante a animação faz a subida engasgar. Mesma
// estratégia do hub, do rewards e da loja.
const mounted = ref(false);

function close() {
  store.closeLevels();
}
</script>

<template>
  <LoyaltyHubSheet
    desktop-max-width="1100px"
    mobile-content-padding-x="0px"
    :open="isLevelsOpen"
    :panel-background="panelBackground"
    :particles-intensity="0"
    @close="close"
    @closed="mounted = false"
    @opened="mounted = true"
  >
    <!-- Wrapper meu, e não classe direto na view: é por aqui que o `:deep`
         alcança a árvore delas. -->
    <div
      v-if="mounted"
      class="lhlv"
    >
      <SmarticoAlertsAndLoader
        v-if="!canRender"
        :force-show-error="!isGamificationLevelsActived"
      />

      <LevelsDesktopView v-else-if="isDesktop" />

      <LevelsMobileView v-else />
    </div>

    <div
      v-else
      class="lhlv-loading"
    ></div>
  </LoyaltyHubSheet>
</template>

<style scoped lang="scss">
// As views se desenham pra ser a PÁGINA: a de desktop pinta o próprio fundo
// (gradiente radial com o glow do elo ancorado no topo) e a página as envolve
// com margens NEGATIVAS pra cancelar o padding do layout. Dentro do painel esse
// padding não existe — sem zerar as margens o conteúdo vazaria pelas laterais.
// O alvo é por posição (`> *`) e não por classe: as views não têm classe de
// gancho, e criar uma só pra isto amarraria elas a este consumidor.
// O fundo próprio da view sai de cena: quem pinta o glow agora é o painel, e os
// dois juntos dariam duas camadas do mesmo brilho — a de baixo cortada na linha
// da topbar, que é justamente a emenda que a mudança veio tirar.
.lhlv {
  width: 100%;

  :deep(> *) {
    max-width: none !important;
    width: 100% !important;
    margin: 0 !important;
    background: transparent !important;
  }
}

// Vão do slide-up: só o espaço, sem esqueleto. Diferente do rewards e da loja,
// aqui o conteúdo não é uma grade previsível — um esqueleto que não parece com
// o que vem depois chama mais atenção que o vazio.
// As fagulhas da própria view, atenuadas — só aqui dentro. Na rota elas sobem
// atrás de um fundo que ocupa a tela toda; no painel a mesma faixa de 300px
// (mobile) / 620px (desktop) fica em cima de um bloco estreito e de conteúdo
// denso, e o pico de 0.55 da animação lê como camada, não como fundo.
// A opacidade vai no CONTAINER e não nas fagulhas: a animação `levelsDrift`
// anima `opacity`, então um valor no próprio ponto seria sobrescrito a cada
// frame. `:has(> .levels-drift)` é o gancho porque a camada não tem classe
// própria em nenhuma das duas views — e criar uma só pra isto amarraria elas a
// este consumidor, como no `> *` acima.
.lhlv :deep([aria-hidden='true']:has(> .levels-drift)) {
  opacity: 0.5;
}

.lhlv-loading {
  min-height: 60vh;
}
</style>
